import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { paymentService } from '../services/payments';
import { documentService } from '../services/documents';
import { changeRequestService } from '../services/changeRequests';
import { institutionService } from '../services/institutions';
import { Payment } from '../types/payment';
import { Document } from '../types/document';
import { ChangeRequest } from '../types/changeRequest';
import { InstitutionDto } from '../types/institution';
import { InstitutionDashboardViewModel } from '../types/dashboard';
import { useAuth } from './useAuth';
import { buildInstitutionDashboardViewModel } from '../utils/institutionDashboardViewModel';

export const useInstitutionDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [institution, setInstitution] = useState<InstitutionDto | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [viewModel, setViewModel] = useState<InstitutionDashboardViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setLoading(false);
      setViewModel(null);
      setHasLoaded(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [institutionData, paymentsData, docs, requests] = await Promise.all([
        institutionService.getMyInstitution().catch(() => null),
        paymentService.getMyPayments().catch(() => []),
        documentService.getMyDocuments().catch(() => []),
        changeRequestService.getMyChangeRequests().catch(() => []),
      ]);

      setInstitution(institutionData);
      setPayments(paymentsData);
      setDocuments(docs);
      setChangeRequests(requests);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading institution dashboard data:', err);
      setError(i18n.t('dashboard.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user, i18n]);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;

    const run = async () => {
      if (!userId) {
        setLoading(false);
        setViewModel(null);
        setHasLoaded(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [institutionData, paymentsData, docs, requests] = await Promise.all([
          institutionService.getMyInstitution().catch(() => null),
          paymentService.getMyPayments().catch(() => []),
          documentService.getMyDocuments().catch(() => []),
          changeRequestService.getMyChangeRequests().catch(() => []),
        ]);

        if (cancelled) {
          return;
        }

        setInstitution(institutionData);
        setPayments(paymentsData);
        setDocuments(docs);
        setChangeRequests(requests);
        setHasLoaded(true);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading institution dashboard data:', err);
          setError(i18n.t('dashboard.errors.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the authenticated user changes — not on language/t updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user || !hasLoaded) {
      return;
    }
    setViewModel(
      buildInstitutionDashboardViewModel(
        {
          user,
          institution,
          payments,
          documents,
          changeRequests,
          language: i18n.language,
        },
        t
      )
    );
  }, [
    user,
    institution,
    payments,
    documents,
    changeRequests,
    hasLoaded,
    i18n.language,
    t,
  ]);

  return {
    viewModel,
    institution,
    payments,
    documents,
    changeRequests,
    loading,
    error,
    reload,
  };
};
